import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Subject, Subscription, fromEvent, interval } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { DataServiceService } from './data-service.service';
import { AuthService } from './auth.service';
import { SellerMessage } from '../interface/seller-message.interface';
import UserUtils from '../utils/user.utils';

@Injectable({ providedIn: 'root' })
export class MessagingService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private pollSub?: Subscription;

  readonly unreadCount$ = new BehaviorSubject<number>(0);
  readonly messages$    = new BehaviorSubject<SellerMessage[]>([]);
  readonly loading$     = new BehaviorSubject<boolean>(false);

  private readonly POLL_MS = 60_000;

  constructor(
    private dataService: DataServiceService,
    private authService: AuthService,
  ) {}

  /**
   * Starts the unread-count polling. Call once from AuthLayoutComponent.ngOnInit.
   * Pauses while the tab is hidden and refreshes immediately on return, so
   * background tabs don't keep hitting the backend.
   */
  start(): void {
    if (this.pollSub) return;

    const user = this.authService.getUser();
    if (!user || user.securityLevel < UserUtils.SECURITY_LEVEL_SALES) return;

    this.pollSub = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => document.visibilityState === 'visible'),
      distinctUntilChanged(),
      switchMap(visible => visible ? interval(this.POLL_MS).pipe(startWith(0)) : EMPTY),
      takeUntil(this.destroy$),
      switchMap(() => this.dataService.getSellerUnreadCount()),
    ).subscribe({
      next: res => this.unreadCount$.next(res.count),
      error: ()  => {},
    });
  }

  /** Stops polling. Call from AuthLayoutComponent.ngOnDestroy. */
  stop(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.unreadCount$.next(0);
    this.messages$.next([]);
  }

  /** Fetches the latest 10 messages and updates messages$. */
  loadMessages(): void {
    this.loading$.next(true);
    this.dataService.getSellerMessages({ limit: 10 }).subscribe({
      next: res => {
        this.messages$.next(res.messages);
        this.loading$.next(false);
      },
      error: () => this.loading$.next(false),
    });
  }

  /** Marks one message as read locally and on the server. */
  markAsRead(idTransactionHistory: number): void {
    const prev = this.messages$.value.find(m => m.idTransactionHistory === idTransactionHistory);
    if (prev?.isRead) return;

    this.dataService.markSellerMessageRead(idTransactionHistory).subscribe({
      next: () => {
        this.messages$.next(
          this.messages$.value.map(m =>
            m.idTransactionHistory === idTransactionHistory ? { ...m, isRead: true } : m,
          ),
        );
        this.unreadCount$.next(Math.max(0, this.unreadCount$.value - 1));
      },
    });
  }

  /** Marks all messages as read locally and on the server. */
  markAllAsRead(): void {
    this.dataService.markAllSellerMessagesRead().subscribe({
      next: () => {
        this.messages$.next(this.messages$.value.map(m => ({ ...m, isRead: true })));
        this.unreadCount$.next(0);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
